import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { Button, Icon, Loader, Menus, Text } from '@Sage/hoi-poi-ui';

import { BillboardActions, DetailActions } from 'actions';
import { BILLBOARD } from 'constants/Entities';
import EntityCrud from 'containers/components/EntityCrud';
import EntityCrudContent from 'containers/components/EntityCrud/EntityCrudContent';
import EntityCrudGeneric from 'containers/components/EntityCrud/EntityCrudGeneric';
import EntityCrudWrapper from 'containers/components/EntityCrud/EntityCrudWrapper';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const BillboardCrudHeader = memo(({ isEdition, loadingSave, onCancel, onSave }) => {
    const dispatch = useDispatch();

    const handleSaveChanges = useCallback(() => {
        dispatch(BillboardActions.activateRecompute(false));
        onSave?.();
    }, [dispatch, onSave]);

    const handleSaveAndResend = useCallback(() => {
        dispatch(BillboardActions.activateRecompute(true));
        dispatch(BillboardActions.setBillboardPreviewPending(true));
        dispatch(DetailActions.hideDetail());
        onSave?.();
    }, [dispatch, onSave]);

    const handlePost = useCallback(() => {
        dispatch(BillboardActions.activateRecompute(true));
        dispatch(BillboardActions.setBillboardPreviewPending(true));
        dispatch(DetailActions.hideDetail());
        onSave?.();
    }, [dispatch, onSave]);

    const saveMenuItems = useMemo(
        () => [
            {
                title: <Text type="h6">{getLiteral('action_save_changes_billboard')}</Text>,
                description: (
                    <Text type="caption">{getLiteral('label_save_changes_billboard')}</Text>
                ),
                onClick: handleSaveChanges,
            },
            {
                title: <Text type="h6">{getLiteral('action_save_resend_changes_billboard')}</Text>,
                description: (
                    <Text type="caption">{getLiteral('label_save_resend_changes_billboard')}</Text>
                ),
                onClick: handleSaveAndResend,
            },
        ],
        [handleSaveChanges, handleSaveAndResend],
    );

    const saveMenuTrigger = useMemo(
        () => (
            <Button
                className="fm-button-header__save"
                customIcon={<Icon name="chevron" size="small" />}
                iconPosition="right"
                isDisabled={loadingSave}
                isLoading={loadingSave}
            >
                {getLiteral('action_save')}
            </Button>
        ),
        [loadingSave],
    );

    return (
        <div className="entity-crud-buttons billboard-crud-header-actions">
            <Button className="fm-button-header__cancel" onClick={onCancel} type="terciary">
                {getLiteral('action_cancel')}
            </Button>
            {!isEdition && (
                <Button
                    className="fm-button-header__save"
                    isDisabled={loadingSave}
                    isLoading={loadingSave}
                    onClick={handlePost}
                >
                    {getLiteral('action_add')}
                </Button>
            )}
            {isEdition && (
                <Menus
                    className="billboard-crud-save-menu"
                    classNameMenu="billboard-crud-save-menu__popover"
                    items={saveMenuItems}
                    placement="bottom-end"
                    size="large"
                    title={saveMenuTrigger}
                />
            )}
        </div>
    );
});

BillboardCrudHeader.displayName = 'BillboardCrudHeader';

BillboardCrudHeader.propTypes = {
    isEdition: PropTypes.bool,
    loadingSave: PropTypes.bool,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
};

const BillboardCrud = memo(() => {
    const dispatch = useDispatch();

    const getCrudTitle = useCallback((loading, id) => {
        if (loading) return null;
        return !id
            ? getLiteral('title_billboard_new_post')
            : getLiteral('title_billboard_edit_post');
    }, []);

    const handleSaveSuccess = useCallback(
        (result) => {
            if (!result) return;
            dispatch(BillboardActions.setBillboardPreview(false));
        },
        [dispatch],
    );

    const onDrop = useCallback(
        (files) => {
            if (files?.length) {
                dispatch(BillboardActions.addFilesToUpload(files));
            }
        },
        [dispatch],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, noClick: true });

    return (
        <EntityCrud
            className="billboard-entity-crud"
            entity={BILLBOARD}
            hasCrudInDetail={false}
            onSave={handleSaveSuccess}
            width={500}
        >
            {({ data, onClose, onSave, loading, loadingSave, renderLoader }) => {
                const handleClose = () => {
                    dispatch(BillboardActions.setBillboardPreview(false));
                    onClose();
                };

                return (
                    <EntityCrudWrapper>
                        <div className="entity-crud-header billboard-entity-crud-header">
                            <div className="entity-crud-title billboard-entity-crud-title">
                                {getCrudTitle(loading, data?.id)}
                            </div>
                            <BillboardCrudHeader
                                isEdition={!!data?.id}
                                loadingSave={loadingSave}
                                onCancel={handleClose}
                                onSave={onSave}
                            />
                        </div>
                        <EntityCrudContent>
                            {loading && renderLoader()}
                            {!loading && (
                                <div
                                    {...getRootProps({
                                        className: 'billboard-dropzone',
                                    })}
                                >
                                    <input {...getInputProps()} />
                                    {isDragActive && <div className="billboard-crud-drag" />}
                                    {loadingSave && (
                                        <div className="billboard-crud-spinner">
                                            <Loader />
                                        </div>
                                    )}
                                    <EntityCrudGeneric formWidth="100%" />
                                </div>
                            )}
                        </EntityCrudContent>
                    </EntityCrudWrapper>
                );
            }}
        </EntityCrud>
    );
});

BillboardCrud.displayName = 'BillboardCrud';

export default BillboardCrud;
